import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaChartLine, FaExchangeAlt } from "react-icons/fa";
import "../styles/AnalyseSection.css";
import { apiFetch } from "../utils/api";
import { formatDateWithWeekday } from "../utils/dateFormat";
import { getMarkColorName, getMarkColorStyle } from "../utils/markColors";
import { MarkColorBadge } from "./MarkColorPicker";
import { useNotifications } from "./NotificationProvider";

const formatCurrency = (value) => `${Number(value || 0).toFixed(2).replace(".", ",")} €`;
const formatNumber = (value) => Number(value || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });
const formatPercent = (value) => `${Number(value || 0).toFixed(2).replace(".", ",")} %`;
const hasValue = (value) => Math.abs(Number(value || 0)) > 0.000001;
const hasDayActivity = (day) => (
  hasValue(day.total)
  || hasValue(day.drinkTotal)
  || hasValue(day.depositTotal)
  || hasValue(day.voucherTotal)
  || hasValue(day.customTotal)
  || hasValue(day.drinkQuantity)
  || hasValue(day.revenueShare)
  || hasValue(day.tip)
);
const hasDrinkActivity = (drink) => hasValue(drink.quantity) || hasValue(drink.total);

const DrinkName = ({ label, color, className = "" }) => (
  <span className={`analyse-drink-name ${className}`.trim()}>
    <MarkColorBadge value={color} />
    <span>{label || "-"}</span>
  </span>
);

const getDrinkOptionLabel = (drink) => `${drink.label || "-"} · ${getMarkColorName(drink.color)}`;

export const aggregateDetail = (detail, selectedUnitKeys = []) => {
  if (!detail) return null;
  const isBedienung = detail.viewerAccountType === "bedienung";
  const unitFilter = new Set(selectedUnitKeys);
  const allowUnit = (unitKey) => unitFilter.size === 0 || unitFilter.has(unitKey);
  const dayMap = new Map((detail.days || []).map(day => [Number(day.id), {
    ...day,
    total: 0,
    drinkTotal: 0,
    depositTotal: 0,
    voucherTotal: 0,
    customTotal: 0,
    drinkQuantity: 0,
    revenueShare: 0,
    tip: 0,
    walletWithoutChange: 0,
    customerRevenue: 0,
  }]));
  const drinkMap = new Map();
  const shareMap = new Map();
  const unitMap = new Map((detail.units || []).map(unit => [unit.unit_key, {
    ...unit,
    total: 0,
    drinkTotal: 0,
    depositTotal: 0,
    voucherTotal: 0,
    customTotal: 0,
    drinkQuantity: 0,
    revenueShare: 0,
    tip: 0,
    walletWithoutChange: 0,
    customerRevenue: 0,
  }]));

  (detail.dayUnitTotals || []).forEach(row => {
    if (!allowUnit(row.unit_key)) return;
    const day = dayMap.get(Number(row.tag_id));
    if (day) {
      day.total += isBedienung ? 0 : Number(row.total || 0);
      day.drinkTotal += Number(row.drinkTotal || 0);
      day.depositTotal += Number(row.depositTotal || 0);
      day.voucherTotal += Number(row.voucherTotal || 0);
      day.customTotal += Number(row.customTotal || 0);
      day.drinkQuantity += isBedienung ? 0 : Number(row.drinkQuantity || 0);
    }
    const unit = unitMap.get(row.unit_key);
    if (unit) {
      unit.total += isBedienung ? 0 : Number(row.total || 0);
      unit.drinkTotal += Number(row.drinkTotal || 0);
      unit.depositTotal += Number(row.depositTotal || 0);
      unit.voucherTotal += Number(row.voucherTotal || 0);
      unit.customTotal += Number(row.customTotal || 0);
      unit.drinkQuantity += isBedienung ? 0 : Number(row.drinkQuantity || 0);
    }
  });

  (detail.earningRows || []).forEach(row => {
    if (!allowUnit(row.unit_key)) return;
    const day = dayMap.get(Number(row.tag_id));
    if (day) {
      day.total += Number(row.payout || 0);
      day.revenueShare += Number(row.ownRevenueShare || 0);
      day.tip += Number(row.ownTip || 0);
      day.walletWithoutChange += Number(row.walletWithoutChange || 0);
      day.customerRevenue += Number(row.ownCustomerRevenue || 0);
      day.drinkQuantity += isBedienung ? Number(row.drinkQuantity || 0) : 0;
    }
    const unit = unitMap.get(row.unit_key);
    if (unit) {
      unit.total += Number(row.payout || 0);
      unit.revenueShare += Number(row.ownRevenueShare || 0);
      unit.tip += Number(row.ownTip || 0);
      unit.walletWithoutChange += Number(row.walletWithoutChange || 0);
      unit.customerRevenue += Number(row.ownCustomerRevenue || 0);
      unit.drinkQuantity += isBedienung ? Number(row.drinkQuantity || 0) : 0;
    }
    (row.shareRows || []).forEach(shareRow => {
      const share = shareMap.get(shareRow.key) || {
        key: shareRow.key,
        label: shareRow.label,
        color: shareRow.color,
        sold: 0,
        sharePrice: Number(shareRow.sharePrice || 0),
        gain: 0,
      };
      share.sold += Number(shareRow.sold || 0);
      share.gain += Number(shareRow.gain || 0);
      shareMap.set(shareRow.key, share);
    });
  });

  (detail.drinkRows || []).forEach(row => {
    if (!allowUnit(row.unit_key)) return;
    const current = drinkMap.get(row.drink_key) || {
      drinkKey: row.drink_key,
      label: row.label,
      color: row.farbe,
      quantity: 0,
      total: 0,
      daily: new Map(),
    };
    current.quantity += Number(row.quantity || 0);
    current.total += Number(row.total || 0);
    const daily = current.daily.get(Number(row.tag_id)) || {
      tagId: Number(row.tag_id),
      datum: row.datum,
      quantity: 0,
      total: 0,
    };
    daily.quantity += Number(row.quantity || 0);
    daily.total += Number(row.total || 0);
    current.daily.set(Number(row.tag_id), daily);
    drinkMap.set(row.drink_key, current);
  });

  const days = [...dayMap.values()].sort((a, b) => String(a.datum).localeCompare(String(b.datum)));
  const drinks = [...drinkMap.values()]
    .map(drink => ({ ...drink, daily: [...drink.daily.values()] }))
    .sort((a, b) => b.quantity - a.quantity || b.total - a.total);
  const units = [...unitMap.values()].filter(unit => unitFilter.size === 0 || unitFilter.has(unit.unit_key));
  const total = days.reduce((sum, day) => sum + day.total, 0);
  const drinkTotal = days.reduce((sum, day) => sum + day.drinkTotal, 0);
  const depositTotal = days.reduce((sum, day) => sum + day.depositTotal, 0);
  const voucherTotal = days.reduce((sum, day) => sum + day.voucherTotal, 0);
  const customTotal = days.reduce((sum, day) => sum + day.customTotal, 0);
  const drinkQuantity = days.reduce((sum, day) => sum + day.drinkQuantity, 0);
  const revenueShare = days.reduce((sum, day) => sum + Number(day.revenueShare || 0), 0);
  const tip = days.reduce((sum, day) => sum + Number(day.tip || 0), 0);
  const walletWithoutChange = days.reduce((sum, day) => sum + Number(day.walletWithoutChange || 0), 0);
  const customerRevenue = days.reduce((sum, day) => sum + Number(day.customerRevenue || 0), 0);
  const tipPercent = customerRevenue > 0 ? (tip / customerRevenue) * 100 : 0;
  const missingWalletCount = (detail.earningRows || [])
    .filter(row => allowUnit(row.unit_key) && !row.hasWallet)
    .length;
  const billedDays = days.filter(hasDayActivity).length;

  return {
    days,
    drinks,
    shareRows: [...shareMap.values()].sort((a, b) => b.gain - a.gain),
    units,
    total,
    drinkTotal,
    depositTotal,
    voucherTotal,
    customTotal,
    drinkQuantity,
    revenueShare,
    tip,
    walletWithoutChange,
    customerRevenue,
    tipPercent,
    missingWalletCount,
    billedDays,
    topDrink: drinks.find(hasDrinkActivity),
  };
};

export const buildSeries = (detail, aggregate, selectedDrinkKey, metric) => {
  if (!detail || !aggregate) return [];
  const activeDays = aggregate.days.filter(hasDayActivity);
  if (selectedDrinkKey === "all") {
    return activeDays.map(day => ({
      label: formatDateWithWeekday(day.datum),
      shortLabel: String(day.datum || "").slice(5),
      value: metric === "quantity"
        ? day.drinkQuantity
        : (detail.viewerAccountType === "bedienung" ? day.total : day.drinkTotal),
    }));
  }

  const drink = aggregate.drinks.find(item => item.drinkKey === selectedDrinkKey);
  const dailyByTag = new Map((drink?.daily || []).map(day => [Number(day.tagId), day]));
  return activeDays.map(day => {
    const daily = dailyByTag.get(Number(day.id));
    return {
      label: formatDateWithWeekday(day.datum),
      shortLabel: String(day.datum || "").slice(5),
      value: metric === "quantity" ? Number(daily?.quantity || 0) : Number(daily?.total || 0),
    };
  });
};

const LineChart = ({ data, metric }) => {
  const max = Math.max(...data.map(item => item.value), 0);
  const width = 720;
  const height = 240;
  const points = data.map((item, index) => {
    const x = data.length <= 1 ? width / 2 : 32 + (index * (width - 64)) / (data.length - 1);
    const y = max > 0 ? height - 34 - (item.value / max) * (height - 76) : height - 34;
    return { ...item, x, y };
  });
  const path = points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");

  return (
    <div className="analyse-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Analysegraph">
        <line x1="28" y1={height - 34} x2={width - 20} y2={height - 34} />
        <line x1="28" y1="18" x2="28" y2={height - 34} />
        {path && <path d={path} className="analyse-chart-line" />}
        {points.map(point => (
          <g key={point.label}>
            <circle cx={point.x} cy={point.y} r="5" />
            <text x={point.x} y={height - 10} textAnchor="middle">{point.shortLabel}</text>
            <text x={point.x} y={Math.max(14, point.y - 10)} textAnchor="middle">
              {metric === "quantity" ? formatNumber(point.value) : formatCurrency(point.value)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const BarChart = ({ data, valueFormatter = formatNumber, renderLabel }) => {
  const max = Math.max(...data.map(item => item.quantity), 0);
  return (
    <div className="analyse-bars">
      {data.slice(0, 8).map(item => (
        <div className="analyse-bar-row" key={item.drinkKey}>
          {renderLabel ? renderLabel(item) : <span>{item.label}</span>}
          <div className="analyse-bar-track">
            <div style={{ width: `${max > 0 ? (item.quantity / max) * 100 : 0}%` }} />
          </div>
          <strong>{valueFormatter(item.quantity)}</strong>
        </div>
      ))}
    </div>
  );
};

const AnalyseSection = () => {
  const { showNotification } = useNotifications();
  const currentYear = new Date().getFullYear();
  const [events, setEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [detailsById, setDetailsById] = useState({});
  const [selectedUnitKeys, setSelectedUnitKeys] = useState([]);
  const [selectedDrinkKey, setSelectedDrinkKey] = useState("all");
  const [chartMetric, setChartMetric] = useState("revenue");
  const [compareMode, setCompareMode] = useState(false);
  const [compareLeftId, setCompareLeftId] = useState("");
  const [compareRightId, setCompareRightId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiFetch("/api/analyse/events")
      .then(data => {
        setEvents(data);
        if (data.length) {
          setSelectedYear(new Date(data[data.length - 1].start_date).getFullYear());
        }
      })
      .catch(err => {
        console.error(err);
        if (!err.sessionExpired) showNotification("error", err.message);
      })
      .finally(() => setLoading(false));
  }, [showNotification]);

  const loadDetail = useCallback(async (eventId) => {
    if (!eventId || detailsById[eventId]) return detailsById[eventId];
    const detail = await apiFetch(`/api/analyse/events/${eventId}/detail`);
    setDetailsById(prev => ({ ...prev, [eventId]: detail }));
    return detail;
  }, [detailsById]);

  useEffect(() => {
    if (!selectedEventId) return;
    setLoading(true);
    loadDetail(selectedEventId)
      .catch(err => {
        console.error(err);
        if (!err.sessionExpired) showNotification("error", err.message);
      })
      .finally(() => setLoading(false));
  }, [loadDetail, selectedEventId, showNotification]);

  useEffect(() => {
    [compareLeftId, compareRightId].filter(Boolean).forEach(eventId => {
      loadDetail(eventId).catch(err => {
        console.error(err);
        if (!err.sessionExpired) showNotification("error", err.message);
      });
    });
  }, [compareLeftId, compareRightId, loadDetail, showNotification]);

  const filteredEvents = useMemo(
    () => events.filter(event => new Date(event.start_date).getFullYear() === selectedYear),
    [events, selectedYear]
  );
  const selectedDetail = selectedEventId ? detailsById[selectedEventId] : null;
  const aggregate = useMemo(
    () => aggregateDetail(selectedDetail, selectedUnitKeys),
    [selectedDetail, selectedUnitKeys]
  );
  const series = useMemo(
    () => buildSeries(selectedDetail, aggregate, selectedDrinkKey, chartMetric),
    [selectedDetail, aggregate, selectedDrinkKey, chartMetric]
  );

  useEffect(() => {
    if (
      selectedDrinkKey !== "all"
      && !aggregate?.drinks.some(drink => drink.drinkKey === selectedDrinkKey && hasDrinkActivity(drink))
    ) {
      setSelectedDrinkKey("all");
    }
  }, [aggregate, selectedDrinkKey]);

  const openEvent = (eventId) => {
    setSelectedEventId(eventId);
    setSelectedUnitKeys([]);
    setSelectedDrinkKey("all");
    setCompareMode(false);
  };

  const toggleUnit = (unitKey) => {
    setSelectedUnitKeys(prev => prev.includes(unitKey)
      ? prev.filter(key => key !== unitKey)
      : [...prev, unitKey]
    );
  };

  const renderOverview = () => (
    <div className="analyse-section">
      <div className="analyse-toolbar">
        <div className="year-carousel">
          <button className="year-nav" onClick={() => setSelectedYear(prev => prev - 1)}>&#8592;</button>
          <div className="year-display-wrapper">
            <div className="year-display">{selectedYear}</div>
          </div>
          <button className="year-nav" onClick={() => setSelectedYear(prev => prev + 1)}>&#8594;</button>
        </div>
        <button type="button" className="analyse-primary-button" onClick={() => setCompareMode(prev => !prev)}>
          <FaExchangeAlt /> Events vergleichen
        </button>
      </div>

      {compareMode && renderCompare()}

      {loading && <p className="analyse-empty">Lädt...</p>}
      {!loading && filteredEvents.length === 0 && <p className="analyse-empty">Für dieses Jahr sind keine Analysedaten vorhanden.</p>}
      <div className="tile-grid">
        {filteredEvents.map(event => (
          <div className="tile event-tile analyse-event-tile" key={event.id} onClick={() => openEvent(event.id)}>
            <div className="event-tile-content">
              <h3>{event.name}</h3>
              <p>{formatDateWithWeekday(event.start_date)} - {formatDateWithWeekday(event.end_date)}</p>
              <strong>{formatCurrency(event.total)}</strong>
              <small>{event.viewerAccountType === "bedienung" ? `${formatCurrency(event.tip)} Trinkgeld` : `Abzurechnen · ${formatCurrency(event.drinkTotal)} Getränkeumsatz · ${formatNumber(event.drinkQuantity)} Getränke`}</small>
              {event.veranstalter && <p>Veranstalter: {event.veranstalter}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCompare = () => {
    const left = aggregateDetail(detailsById[compareLeftId]);
    const right = aggregateDetail(detailsById[compareRightId]);
    const leftEvent = events.find(event => Number(event.id) === Number(compareLeftId));
    const rightEvent = events.find(event => Number(event.id) === Number(compareRightId));
    const compareSeries = [left, right].map((data, index) => ({
      label: index === 0 ? leftEvent?.name : rightEvent?.name,
      value: data?.total || 0,
    }));

    return (
      <section className="analyse-compare-panel">
        <div className="analyse-filter-row">
          <select value={compareLeftId} onChange={e => setCompareLeftId(e.target.value)}>
            <option value="">Erstes Event</option>
            {events.map(event => <option key={event.id} value={event.id}>{event.name} ({new Date(event.start_date).getFullYear()})</option>)}
          </select>
          <select value={compareRightId} onChange={e => setCompareRightId(e.target.value)}>
            <option value="">Zweites Event</option>
            {events.map(event => <option key={event.id} value={event.id}>{event.name} ({new Date(event.start_date).getFullYear()})</option>)}
          </select>
        </div>
        {left && right && (
          <>
            <div className="analyse-summary-grid">
              <div>
                <span>{leftEvent?.name}</span>
                <strong>{formatCurrency(left.total)}</strong>
                <small>{formatCurrency(left.drinkTotal)} Getränkeumsatz · {formatNumber(left.drinkQuantity)} Getränke</small>
              </div>
              <div>
                <span>{rightEvent?.name}</span>
                <strong>{formatCurrency(right.total)}</strong>
                <small>{formatCurrency(right.drinkTotal)} Getränkeumsatz · {formatNumber(right.drinkQuantity)} Getränke</small>
              </div>
              <div>
                <span>Differenz</span>
                <strong>{formatCurrency(right.total - left.total)}</strong>
                <small>{formatNumber(right.drinkQuantity - left.drinkQuantity)} Getränke</small>
              </div>
            </div>
            <BarChart
              valueFormatter={formatCurrency}
              data={compareSeries.map(item => ({
              drinkKey: item.label,
              label: item.label,
              quantity: item.value,
            }))}
            />
          </>
        )}
      </section>
    );
  };

  const renderDetail = () => {
    if (!selectedDetail || !aggregate) return <p className="analyse-empty">Analyse wird geladen...</p>;
    const allUnitsSelected = selectedUnitKeys.length === 0;
    const isBedienung = selectedDetail.viewerAccountType === "bedienung";
    const activeDays = aggregate.days.filter(hasDayActivity);
    const activeDrinks = aggregate.drinks.filter(hasDrinkActivity);
    const activeUnits = aggregate.units.filter(unit => hasDayActivity(unit));
    const selectedDrink = selectedDrinkKey === "all"
      ? null
      : activeDrinks.find(drink => drink.drinkKey === selectedDrinkKey);
    const dailyDrinkRows = activeDrinks
      .filter(drink => selectedDrinkKey === "all" || drink.drinkKey === selectedDrinkKey)
      .flatMap(drink => drink.daily.map(day => ({
        ...day,
        drinkKey: drink.drinkKey,
        label: drink.label,
        color: drink.color,
      })))
      .filter(row => hasValue(row.quantity) || hasValue(row.total))
      .sort((a, b) => String(a.datum).localeCompare(String(b.datum)) || a.label.localeCompare(b.label));

    const bedienungDetail = (
      <div className="analyse-detail">
        <div className="analyse-detail-header">
          <button type="button" className="analyse-icon-button" onClick={() => setSelectedEventId(null)} aria-label="Zurück">
            <FaArrowLeft />
          </button>
          <div>
            <h2>{selectedDetail.event.name}</h2>
            <p>{formatDateWithWeekday(selectedDetail.event.start_date)} - {formatDateWithWeekday(selectedDetail.event.end_date)}</p>
          </div>
        </div>

        <div className="analyse-summary-grid">
          <div><span>Verdient</span><strong>{formatCurrency(aggregate.total)}</strong></div>
          <div><span>Umsatzanteil</span><strong>{formatCurrency(aggregate.revenueShare)}</strong></div>
          <div>
            <span>Trinkgeld</span>
            <strong>{formatCurrency(aggregate.tip)}</strong>
            {aggregate.missingWalletCount > 0 && <small>Geldbeutelwerte fehlen</small>}
          </div>
          <div><span>Trinkgeld auf eigenen Umsatz</span><strong>{formatPercent(aggregate.tipPercent)}</strong></div>
        </div>

        <section className="analyse-panel">
          <div className="analyse-panel-title">
            <h3>Filter</h3>
            <button type="button" onClick={() => setSelectedUnitKeys([])}>Alle anzeigen</button>
          </div>
          <div className="analyse-unit-filter">
            {(selectedDetail.units || []).map(unit => (
              <button
                key={unit.unit_key}
                type="button"
                className={allUnitsSelected || selectedUnitKeys.includes(unit.unit_key) ? "active" : ""}
                onClick={() => toggleUnit(unit.unit_key)}
              >
                {unit.unit_type === "team" ? "Team" : "Einzel"}: {unit.name}
              </button>
            ))}
          </div>
        </section>

        <section className="analyse-panel">
          <div className="analyse-panel-title">
            <h3><FaChartLine /> Verdienst</h3>
          </div>
          <LineChart data={series} metric="revenue" />
        </section>

        <div className="analyse-detail-grid">
          <section className="analyse-panel">
            <h3>Tage</h3>
            <table className="analyse-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Verdient</th>
                  <th>Umsatzanteil</th>
                  <th>Trinkgeld</th>
                </tr>
              </thead>
              <tbody>
                {activeDays.map(day => (
                  <tr key={day.id}>
                    <td data-label="Tag">{formatDateWithWeekday(day.datum)}</td>
                    <td data-label="Verdient">{formatCurrency(day.total)}</td>
                    <td data-label="Umsatzanteil">{formatCurrency(day.revenueShare)}</td>
                    <td data-label="Trinkgeld">{formatCurrency(day.tip)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="analyse-panel">
            <h3>Umsatzanteil</h3>
            <table className="analyse-table">
              <thead>
                <tr>
                  <th>Markenart</th>
                  <th>Verkauft</th>
                  <th>Anteil</th>
                  <th>Gewinn</th>
                </tr>
              </thead>
              <tbody>
                {aggregate.shareRows.map(row => (
                  <tr key={row.key}>
                    <td className="drink-kind-cell" data-label="Markenart" style={getMarkColorStyle(row.color)}>{row.label}</td>
                    <td data-label="Verkauft">{formatNumber(row.sold)} Stk.</td>
                    <td data-label="Anteil">{formatCurrency(row.sharePrice)}</td>
                    <td data-label="Gewinn">{formatCurrency(row.gain)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="analyse-panel">
            <h3>Teams und Einzel</h3>
            <table className="analyse-table">
              <thead>
                <tr>
                  <th>Einheit</th>
                  <th>Verdient</th>
                  <th>Umsatzanteil</th>
                  <th>Trinkgeld</th>
                </tr>
              </thead>
              <tbody>
                {activeUnits.map(unit => (
                  <tr key={unit.unit_key}>
                    <td data-label="Einheit">{unit.unit_type === "team" ? "Team" : "Einzel"}: {unit.name}</td>
                    <td data-label="Verdient">{formatCurrency(unit.total)}</td>
                    <td data-label="Umsatzanteil">{formatCurrency(unit.revenueShare)}</td>
                    <td data-label="Trinkgeld">{formatCurrency(unit.tip)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="analyse-panel">
            <h3>Geldwerte</h3>
            <table className="analyse-table">
              <tbody>
                <tr><td data-label="Kennzahl">Ohne Wechselgeld</td><td data-label="Wert">{formatCurrency(aggregate.walletWithoutChange)}</td></tr>
                <tr><td data-label="Kennzahl">Eigener Kundenumsatz</td><td data-label="Wert">{formatCurrency(aggregate.customerRevenue)}</td></tr>
                <tr><td data-label="Kennzahl">Trinkgeld</td><td data-label="Wert">{formatCurrency(aggregate.tip)}</td></tr>
                <tr><td data-label="Kennzahl">Umsatzanteil</td><td data-label="Wert">{formatCurrency(aggregate.revenueShare)}</td></tr>
                <tr><td data-label="Kennzahl">Verdient</td><td data-label="Wert">{formatCurrency(aggregate.total)}</td></tr>
              </tbody>
            </table>
          </section>
        </div>
      </div>
    );

    if (isBedienung) return bedienungDetail;

    return (
      <div className="analyse-detail">
        <div className="analyse-detail-header">
          <button type="button" className="analyse-icon-button" onClick={() => setSelectedEventId(null)} aria-label="Zurück">
            <FaArrowLeft />
          </button>
          <div>
            <h2>{selectedDetail.event.name}</h2>
            <p>{formatDateWithWeekday(selectedDetail.event.start_date)} - {formatDateWithWeekday(selectedDetail.event.end_date)}</p>
          </div>
        </div>

        <div className="analyse-summary-grid">
          <div>
            <span>Abzurechnen</span>
            <strong>{formatCurrency(aggregate.total)}</strong>
            <small>{aggregate.billedDays} Abrechnungstage</small>
          </div>
          <div>
            <span>Getränkeumsatz</span>
            <strong>{formatCurrency(aggregate.drinkTotal)}</strong>
            <small>ohne Pfand und Abzüge</small>
          </div>
          <div>
            <span>Verkaufte Getränke</span>
            <strong>{formatNumber(aggregate.drinkQuantity)}</strong>
            <small>netto nach Rückgaben</small>
          </div>
          <div>
            <span>Top Getränk</span>
            <strong>
              {aggregate.topDrink ? <DrinkName label={aggregate.topDrink.label} color={aggregate.topDrink.color} /> : "-"}
            </strong>
            {aggregate.topDrink && <small>{formatNumber(aggregate.topDrink.quantity)} verkauft</small>}
          </div>
        </div>

        <section className="analyse-panel">
          <div className="analyse-panel-title">
            <h3>Filter</h3>
            <button type="button" onClick={() => setSelectedUnitKeys([])}>Alle anzeigen</button>
          </div>
          <div className="analyse-unit-filter">
            {(selectedDetail.units || []).map(unit => (
              <button
                key={unit.unit_key}
                type="button"
                className={allUnitsSelected || selectedUnitKeys.includes(unit.unit_key) ? "active" : ""}
                onClick={() => toggleUnit(unit.unit_key)}
              >
                {unit.unit_type === "team" ? "Team" : "Einzel"}: {unit.name}
              </button>
            ))}
          </div>
        </section>

        <section className="analyse-panel">
          <div className="analyse-panel-title">
            <h3><FaChartLine /> Verkaufsverlauf</h3>
            <div className="analyse-filter-row">
              <select value={selectedDrinkKey} onChange={e => setSelectedDrinkKey(e.target.value)}>
                <option value="all">Gesamt</option>
                {activeDrinks.map(drink => (
                  <option key={drink.drinkKey} value={drink.drinkKey}>{getDrinkOptionLabel(drink)}</option>
                ))}
              </select>
              <select value={chartMetric} onChange={e => setChartMetric(e.target.value)}>
                <option value="revenue">Getränkeumsatz</option>
                <option value="quantity">Menge</option>
              </select>
            </div>
          </div>
          <LineChart data={series} metric={chartMetric} />
          {selectedDrink && (
            <p className="analyse-chart-note">
              <DrinkName label={selectedDrink.label} color={selectedDrink.color} />: {formatNumber(selectedDrink.quantity)} verkauft, {formatCurrency(selectedDrink.total)} Getränkeumsatz.
            </p>
          )}
        </section>

        <div className="analyse-detail-grid">
          <section className="analyse-panel">
            <h3>Tage</h3>
            <table className="analyse-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Abzurechnen</th>
                  <th>Getränkeumsatz</th>
                  <th>Getränke</th>
                </tr>
              </thead>
              <tbody>
                {activeDays.map(day => (
                  <tr key={day.id}>
                    <td data-label="Tag">{formatDateWithWeekday(day.datum)}</td>
                    <td data-label="Abzurechnen">{formatCurrency(day.total)}</td>
                    <td data-label="Getränkeumsatz">{formatCurrency(day.drinkTotal)}</td>
                    <td data-label="Getränke">{formatNumber(day.drinkQuantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="analyse-panel">
            <h3>Abrechnung im Überblick</h3>
            <p className="analyse-panel-note">Diese Summe berücksichtigt alle Positionen der Abrechnung.</p>
            <table className="analyse-table">
              <tbody>
                <tr><td data-label="Position">Getränke</td><td data-label="Betrag">{formatCurrency(aggregate.drinkTotal)}</td></tr>
                <tr><td data-label="Position">Pfand</td><td data-label="Betrag">{formatCurrency(aggregate.depositTotal)}</td></tr>
                <tr><td data-label="Position">Marken</td><td data-label="Betrag">{formatCurrency(aggregate.voucherTotal)}</td></tr>
                <tr><td data-label="Position">Sonstiges</td><td data-label="Betrag">{formatCurrency(aggregate.customTotal)}</td></tr>
              </tbody>
              <tfoot>
                <tr><th>Abzurechnen</th><th>{formatCurrency(aggregate.total)}</th></tr>
              </tfoot>
            </table>
          </section>

          <section className="analyse-panel">
            <h3>Getränke gesamt</h3>
            <BarChart
              data={activeDrinks}
              renderLabel={drink => <DrinkName label={drink.label} color={drink.color} />}
            />
            <table className="analyse-table">
              <thead>
                <tr>
                  <th>Getränk</th>
                  <th>Menge</th>
                  <th>Getränkeumsatz</th>
                </tr>
              </thead>
              <tbody>
                {activeDrinks.map(drink => (
                  <tr key={drink.drinkKey}>
                    <td data-label="Getränk"><DrinkName label={drink.label} color={drink.color} /></td>
                    <td data-label="Menge">{formatNumber(drink.quantity)}</td>
                    <td data-label="Getränkeumsatz">{formatCurrency(drink.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="analyse-panel">
            <h3>Getränke je Tag</h3>
            <table className="analyse-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Getränk</th>
                  <th>Menge</th>
                  <th>Getränkeumsatz</th>
                </tr>
              </thead>
              <tbody>
                {dailyDrinkRows.map(row => (
                  <tr key={`${row.tagId}-${row.drinkKey}`}>
                    <td data-label="Tag">{formatDateWithWeekday(row.datum)}</td>
                    <td data-label="Getränk"><DrinkName label={row.label} color={row.color} /></td>
                    <td data-label="Menge">{formatNumber(row.quantity)}</td>
                    <td data-label="Getränkeumsatz">{formatCurrency(row.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="analyse-panel">
            <h3>Teams und Einzel</h3>
            <table className="analyse-table">
              <thead>
                <tr>
                  <th>Einheit</th>
                  <th>Getränkeumsatz</th>
                  <th>Getränke</th>
                </tr>
              </thead>
              <tbody>
                {activeUnits.map(unit => (
                  <tr key={unit.unit_key}>
                    <td data-label="Einheit">{unit.unit_type === "team" ? "Team" : "Einzel"}: {unit.name}</td>
                    <td data-label="Getränkeumsatz">{formatCurrency(unit.drinkTotal)}</td>
                    <td data-label="Getränke">{formatNumber(unit.drinkQuantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    );
  };

  return selectedEventId ? renderDetail() : renderOverview();
};

export default AnalyseSection;
